import {
  UsernameTitle,
  InputContainer,
  UsernameInput,
  UsernameError
} from './Username.styles';

import { isIOS } from '@CS/utils/Device';
import Button from '@CS/components/Button';

import { Popup } from '@CS/components/Popups';
import Arrow from '@CS/assets/arrow.svg?component';
import { useState, useRef, useEffect } from 'react';
import type { PopupProps } from '@CS/components/Popups';
import type { ChangeEvent, CSSProperties, FocusEvent } from 'react';
import { useTranslations } from '@/hooks';

type UsernameProps = PopupProps & {
  onSubmit: (username: string) => Promise<unknown>;
  bold?: boolean;
};

const USERNAME_PATTERN = /^(?=.{3,12}$)[\p{L}0-9_]+$/u;

export const Username = ({
  landscape,
  onSubmit,
  onClose,
  bold,
  ...props
}: UsernameProps) => {
  const buttonId = 'submit';

  const [error, setError] = useState(false);
  const [focus, setFocus] = useState(false);

  const [visible, setVisible] = useState(true);
  const [animate, setAnimate] = useState(true);
  const [username, setUsername] = useState('');

  const [disabled, setDisabled] = useState(true);
  const dialog = useRef<HTMLDialogElement>(null);

  const [title, placeholder, inputError] = useTranslations([
    '11UsernameTitle',
    '11UsernamePlaceholder',
    '11UsernameErrorMessage'
  ]);

  useEffect(() => setDisabled(!username.match(USERNAME_PATTERN)), [username]);

  const onInput = (event: ChangeEvent<HTMLInputElement>) =>
    setUsername(event.target.value);

  const onClick = async () => {
    if (!visible) return;

    try {
      await onSubmit(username);
      setVisible(false);
    } catch {
      setError(true);
    }
  };

  const dialogStyle = {
    ...(focus && {
      transform: `translate(-50%, ${!landscape ? '0' : '-100%'}) scale(1)`,
      top: `${(innerHeight < innerWidth && '2') || ''}5vmax`
    }),

    ...(!animate && { transition: 'none' }),
    WebkitOverflowScrolling: 'touch',
    overscrollBehaviorY: 'none',
    background: 'transparent',
    minHeight: '150px',
    overflow: 'hidden',
    padding: '0 30px',
    width: '100%'
  } as CSSProperties;

  const onFocus = () => {
    if (isIOS) return;
    setAnimate(false);
    setFocus(true);

    setTimeout(() => {
      if (!dialog.current) return;
      dialog.current.scrollTop = 400;
    });
  };

  const onBlur = (event: FocusEvent) => {
    if (event.relatedTarget?.id === buttonId && !isIOS) {
      event.stopPropagation();
      onClick();
    }

    setTimeout(() => setAnimate(true));
    setFocus(false);
  };

  return (
    <Popup
      customStyle={dialogStyle}
      onClose={onClose}
      close={!visible}
      opacity={0.5}
      ref={dialog}
      {...props}
      hideClose
      overlay
    >
      <UsernameTitle>{title}</UsernameTitle>

      <InputContainer $error={error} $bold={bold}>
        <UsernameInput
          placeholder={placeholder}
          onChange={onInput}
          autoComplete="off"
          onFocus={onFocus}
          name="username"
          onBlur={onBlur}
          $bold={bold}
          type="text"
        />

        <Button
          disabled={disabled}
          onClick={onClick}
          purple={bold}
          id={buttonId}
          shadow
          round
        >
          <Arrow />
        </Button>

        {error && <UsernameError>{inputError}</UsernameError>}
      </InputContainer>
    </Popup>
  );
};
